/**
 * Completion Guards
 *
 * These guards ensure users have completed necessary steps in their journey.
 * They handle business logic requirements beyond basic authentication.
 *
 * Guards:
 * - checkPremiumParentRegistration: Ensures premium parents have completed address registration
 * - checkCareTypeRegistration: Ensures premium parents have selected a care type
 * - checkCorporateOnboarding: Ensures corporate parents have completed onboarding
 * - checkSitterOnboarding: Ensures sitters have completed their onboarding process
 */
import { Route, NavigationGuardNext } from 'vue-router';
import { redirectParentRegistration, redirectCareTypeRegistration } from '@/helpers/parentRegistrationChecks';
import { getSharedApolloClient, setupApollo } from '@/lib/apollo_setup';
import { redirectIfNotOnboarded, navigateToRoute } from '@/lib/sitterOnboarding/sitterOnboardingState';
import OnboardingQueries from '@/graphql/onboarding/queries';
import store from '../../store';
import router from '../index';

export const checkPremiumParentRegistration = async (to: Route, _from: Route, next: NavigationGuardNext) => {
  const account = store.getters['account/getAccount'];

  if (account?.isPremium()) {
    try {
      const apolloClient = getSharedApolloClient();
      const redirected = await redirectParentRegistration(apolloClient, to.fullPath);
      if (redirected) {
        next(false);
        return;
      }
    } catch (error) {
      console.error('Error in redirectParentRegistration:', error);
    }
  }

  next();
};

export const checkCareTypeRegistration = async (to: Route, _from: Route, next: NavigationGuardNext) => {
  const account = store.getters['account/getAccount'];

  if (account?.isPremium()) {
    try {
      const apolloClient = getSharedApolloClient();
      await redirectCareTypeRegistration(apolloClient);
    } catch (error) {
      console.error('Error in checkCareTypeRegistration:', error);
    }
  }

  next();
};

export const checkCorporateOnboarding = async (to: Route, _from: Route, next: NavigationGuardNext) => {
  const account = store.getters['account/getAccount'];

  if (account?.isCorporate()) {
    try {
      const apolloClient = getSharedApolloClient();
      const { data } = await apolloClient.query({
        query: OnboardingQueries.FETCH_ONBOARDING_STATUS,
        fetchPolicy: 'network-only',
      });

      if (!data.memberOnboarding.complete) {
        const returnPath = to.fullPath;
        next({
          path: '/corporate-onboarding/care-type',
          query: { return_path: returnPath },
        });
        return;
      }
    } catch (error) {
      console.error('Error checking corporate onboarding:', error);
    }
  }

  next();
};

export const checkSitterOnboarding = async (_to: Route, _from: Route, next: NavigationGuardNext) => {
  const account = store.getters['account/getAccount'];

  if (account?.isSitter()) {
    try {
      const apolloProvider = setupApollo();
      const targetRoute = await redirectIfNotOnboarded(apolloProvider, account, router, _to.path, _to.query);
      if (targetRoute) {
        navigateToRoute(targetRoute, router);
        next(false);
        return;
      }
    } catch (error) {
      console.error('Error in checkSitterOnboarding:', error);
      navigateToRoute('/sitter/signup/address', router);
      next(false);
      return;
    }
  }

  next();
};
